import type {
  OpsCreateTicketReplyRequest,
  OpsCreateTicketReplyResponse,
  OpsListTicketsResponse,
  OpsTicketAttachmentDownloadResponse,
  OpsTicketDetailResponse,
  OpsTicketSortDir,
  OpsTicketSortKey,
  OpsUpdateTicketStatusRequest,
  OpsUpdateTicketStatusResponse,
  TicketStatus,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers around `/v1/ops/tickets*` (#1394).
 *
 * The ops-api routes are role-gated on `ops-admin` OR `ops-support`;
 * unauthorized callers surface 403 FORBIDDEN which `opsApiRequest`
 * throws as `OpsApiError`. The wire is snake_case end to end (ops-api
 * relays platform bodies verbatim), so the query keys below are the
 * exact strings ops-api whitelists — `status` / `org_id` / `q` /
 * `limit` / `offset` / `sort` / `dir`. Empty-string fields are omitted
 * so the downstream treats "not provided" as its default rather than an
 * empty filter.
 */

const BASE_PATH = '/v1/ops/tickets';

export interface OpsTicketsQuery {
  readonly status?: TicketStatus;
  readonly org_id?: string;
  readonly q?: string;
  readonly limit?: number;
  readonly offset?: number;
  readonly sort?: OpsTicketSortKey;
  readonly dir?: OpsTicketSortDir;
}

function buildQueryString(query: OpsTicketsQuery): string {
  const params = new URLSearchParams();
  if (query.status !== undefined) {
    params.set('status', query.status);
  }
  if (query.org_id !== undefined && query.org_id.length > 0) {
    params.set('org_id', query.org_id);
  }
  if (query.q !== undefined && query.q.length > 0) {
    params.set('q', query.q);
  }
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.offset !== undefined) {
    params.set('offset', String(query.offset));
  }
  if (query.sort !== undefined) {
    params.set('sort', query.sort);
  }
  if (query.dir !== undefined) {
    params.set('dir', query.dir);
  }
  const rendered = params.toString();
  return rendered.length > 0 ? `?${rendered}` : '';
}

export async function listTickets(
  query: OpsTicketsQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsListTicketsResponse> {
  const qs = buildQueryString(query);
  return opsApiRequest<OpsListTicketsResponse>(
    `${BASE_PATH}${qs}`,
    { signal },
    fetchImpl,
  );
}

export async function getTicket(
  ticketId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsTicketDetailResponse> {
  return opsApiRequest<OpsTicketDetailResponse>(
    `${BASE_PATH}/${encodeURIComponent(ticketId)}`,
    { signal },
    fetchImpl,
  );
}

export async function downloadTicketAttachment(
  attachmentId: string,
  fetchImpl: typeof fetch,
): Promise<OpsTicketAttachmentDownloadResponse> {
  return opsApiRequest<OpsTicketAttachmentDownloadResponse>(
    `${BASE_PATH}/attachments/${encodeURIComponent(attachmentId)}`,
    {},
    fetchImpl,
  );
}

export async function updateTicketStatus(
  ticketId: string,
  body: OpsUpdateTicketStatusRequest,
  fetchImpl: typeof fetch,
): Promise<OpsUpdateTicketStatusResponse> {
  return opsApiRequest<OpsUpdateTicketStatusResponse>(
    `${BASE_PATH}/${encodeURIComponent(ticketId)}/status`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function createVendorReply(
  ticketId: string,
  body: OpsCreateTicketReplyRequest,
  fetchImpl: typeof fetch,
): Promise<OpsCreateTicketReplyResponse> {
  return opsApiRequest<OpsCreateTicketReplyResponse>(
    `${BASE_PATH}/${encodeURIComponent(ticketId)}/replies`,
    { method: 'POST', body },
    fetchImpl,
  );
}
